import { AdminGroup } from 'Interfaces/Admin';

/**
 *  The editing / access / deletion rights matrix of the group form. Each row is
 *  one role; the three columns map to the three boolean fields on the group.
 *
 *  Kept as data rather than 18 hand-written checkboxes so the grid stays in step
 *  with the model, which is how the fields drifted apart in the JSP template.
 */
export interface RightsRow {
  edit: keyof AdminGroup;
  read: keyof AdminGroup;
  remove: keyof AdminGroup;
  editLabel: string;
  readLabel: string;
  removeLabel: string;
  /** Columns the legacy form rendered as permanently disabled. */
  disabled?: ('edit' | 'read' | 'remove')[];
}

export const RIGHTS_ROWS: RightsRow[] = [
  {
    edit: 'enumerator_edit_only_own_data',
    read: 'enumerator_read_only_own_data',
    remove: 'enumerator_delete_data',
    editLabel: 'enumedit', readLabel: 'enumread', removeLabel: 'enumdelete',
  },
  {
    edit: 'ordinary_edit_only_own_data',
    read: 'ordinary_read_only_own_data',
    remove: 'ordinary_delete_data',
    editLabel: 'ordedit', readLabel: 'ordread', removeLabel: 'orddelete',
  },
  {
    edit: 'viewer_edit_only_own_data',
    read: 'viewer_read_only_own_data',
    remove: 'viewer_delete_data',
    editLabel: 'vieweredit', readLabel: 'viewerread', removeLabel: 'viewerdelete',
    // A viewer can never edit or delete, so those two are shown but locked.
    disabled: ['edit', 'remove'],
  },
  {
    edit: 'task_assignee_edit_only_own_data',
    read: 'task_assignee_read_only_own_data',
    remove: 'task_assignee_delete_data',
    editLabel: 'taskAssigneeEdit', readLabel: 'taskAssigneeRead', removeLabel: 'taskAssigneeDelete',
  },
  {
    edit: 'modeler_edit_only_own_data',
    read: 'modeler_read_only_own_data',
    remove: 'modeler_delete_data',
    editLabel: 'modeleredit', readLabel: 'modelerread', removeLabel: 'modelerdelete',
  },
  {
    edit: 'admin_edit_only_own_data',
    read: 'admin_read_only_own_data',
    remove: 'admin_delete_data',
    editLabel: 'adminedit', readLabel: 'adminread', removeLabel: 'admindelete',
  },
];

/** Rights a brand new group starts with, matching GroupView.render's defaults. */
export const NEW_GROUP_RIGHTS: Partial<AdminGroup> = {
  enumerator_edit_only_own_data: true,
  enumerator_read_only_own_data: true,
  ordinary_delete_data: true,
  viewer_edit_only_own_data: true,
  modeler_delete_data: true,
  admin_delete_data: true,
};

/** The remaining on/off group options, in the order the legacy form showed them. */
export const GROUP_TOGGLES: { field: keyof AdminGroup; label: string }[] = [
  { field: 'surveyMode', label: 'surveyMode' },
  { field: 'enumAddDataOnly', label: 'enumAddDataOnly' },
  { field: 'assigned_location_only', label: 'assignedlocs' },
];

export const GROUP_SHARING_TOGGLES: { field: keyof AdminGroup; label: string }[] = [
  { field: 'shareDataUnregisteredUsers', label: 'ordinaryShareData' },
  { field: 'shareDataUnregisteredUsersViewer', label: 'ordinaryShareDataViewer' },
  { field: 'tableViewEditEnabled', label: 'tableViewEditing' },
  { field: 'mergeDataEnabled', label: 'mergeDataEnabled' },
  { field: 'ordinaryViewFb', label: 'ordinaryViewFb' },
  { field: 'viewerViewFb', label: 'viewerViewFb' },
];
